import React, { useEffect, useRef, useState, memo, useCallback } from "react";
import './MapContainer.css';

const MapContainer = memo(({ startPoint, destination }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const hospitalMarkersRef = useRef([]);
  const directionsRendererRef = useRef(null);
  const movingMarkerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const pulseAnimationsRef = useRef([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [routeProgress, setRouteProgress] = useState(0);
  const [routeDetails, setRouteDetails] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [mapTheme, setMapTheme] = useState('premium');
  const [showTraffic, setShowTraffic] = useState(true);
  const [showHospitals, setShowHospitals] = useState(true);

  // Premium custom icons with gradient effects
  const PREMIUM_ICONS = {
    start: {
      path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
      fillColor: '#10B981',
      fillOpacity: 1,
      strokeColor: '#FFFFFF',
      strokeWeight: 3,
      scale: 2,
      anchor: new window.google.maps.Point(12, 24)
    },
    destination: {
      path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
      fillColor: '#EF4444',
      fillOpacity: 1,
      strokeColor: '#FFFFFF',
      strokeWeight: 3,
      scale: 2,
      anchor: new window.google.maps.Point(12, 24)
    },
    hospital: {
      path: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
      fillColor: '#3B82F6',
      fillOpacity: 0.9,
      strokeColor: '#FFFFFF',
      strokeWeight: 2,
      scale: 1.5,
      anchor: new window.google.maps.Point(12, 12)
    },
    moving: {
      path: 'M13,9V3.5L18.5,9M6,2C4.89,2 4,2.89 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2H6Z',
      fillColor: '#8B5CF6',
      fillOpacity: 1,
      strokeColor: '#FFFFFF',
      strokeWeight: 2,
      scale: 1.8,
      anchor: new window.google.maps.Point(12, 12)
    }
  };

  // Premium map styles with glassmorphism and dark mode
  const MAP_THEMES = {
    premium: [
      {
        featureType: "all",
        elementType: "geometry",
        stylers: [{ color: "#1a1a2e" }]
      },
      {
        featureType: "all",
        elementType: "labels.text.fill",
        stylers: [{ color: "#ffffff" }, { lightness: 16 }]
      },
      {
        featureType: "all",
        elementType: "labels.text.stroke",
        stylers: [{ color: "#1a1a2e" }, { lightness: -40 }]
      },
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ color: "#38396a" }]
      },
      {
        featureType: "road.highway",
        elementType: "geometry.fill",
        stylers: [{ color: "#4f46e5" }]
      },
      {
        featureType: "road.highway",
        elementType: "geometry.stroke",
        stylers: [{ color: "#7c3aed" }]
      },
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#1e1e3f" }]
      },
      {
        featureType: "landscape",
        elementType: "geometry",
        stylers: [{ color: "#16213e" }]
      },
      {
        featureType: "poi",
        elementType: "geometry",
        stylers: [{ color: "#2a2f5f" }]
      }
    ],
    luxury: [
      {
        featureType: "all",
        elementType: "labels.text.fill",
        stylers: [{ saturation: 36 }, { color: "#000000" }, { lightness: 40 }]
      },
      {
        featureType: "all",
        elementType: "labels.text.stroke",
        stylers: [{ visibility: "on" }, { color: "#000000" }, { lightness: 16 }]
      },
      {
        featureType: "all",
        elementType: "labels.icon",
        stylers: [{ visibility: "off" }]
      },
      {
        featureType: "administrative",
        elementType: "geometry.fill",
        stylers: [{ color: "#000000" }, { lightness: 20 }]
      },
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ color: "#000000" }, { lightness: 17 }]
      },
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#000000" }, { lightness: 17 }]
      }
    ]
  };

  const clearMarkers = useCallback(() => {
    // Clear regular markers and their overlays
    // markersRef.current.forEach(item => {
    //   if (item.marker) item.marker.setMap(null);
    //   if (item.overlay) item.overlay.setMap(null);
    // });
    // markersRef.current = [];
  
    // // Clear hospital markers
    // hospitalMarkersRef.current.forEach(marker => marker.setMap(null));
    // hospitalMarkersRef.current = [];
  
    // // Clear moving marker
    // if (movingMarkerRef.current) {
    //   movingMarkerRef.current.setMap(null);
    //   movingMarkerRef.current = null;
    // }
  
    // // Clear pulse animations properly
    // pulseAnimationsRef.current.forEach(animation => {
    //   if (animation.overlay) {
    //     animation.overlay.setMap(null);
    //     // Also call onRemove if it exists to clean up DOM elements
    //     if (typeof animation.overlay.onRemove === 'function') {
    //       animation.overlay.onRemove();
    //     }
    //   }
    // });
    // pulseAnimationsRef.current = [];
  
    // // Clear directions renderer if it exists
    // if (directionsRendererRef.current) {
    //   directionsRendererRef.current.setMap(null);
    //   directionsRendererRef.current = null;
    // }
  }, []);

  const createPremiumPulse = (position, color, size = 'large') => {
    const pulseContainer = document.createElement('div');
    pulseContainer.className = `premium-pulse-container ${size}`;
    pulseContainer.style.cssText = `
      position: absolute;
      pointer-events: none;
      transform: translate(-50%, -50%);
      z-index: 1;
    `;

    const pulse = document.createElement('div');
    pulse.className = 'premium-pulse';
    pulse.style.cssText = `
      width: ${size === 'large' ? '60px' : '40px'};
      height: ${size === 'large' ? '60px' : '40px'};
      border-radius: 50%;
      background: radial-gradient(circle, ${color}40 0%, ${color}20 50%, transparent 70%);
      animation: premiumPulse 2s ease-in-out infinite;
      position: relative;
    `;

    const innerPulse = document.createElement('div');
    innerPulse.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: ${size === 'large' ? '20px' : '15px'};
      height: ${size === 'large' ? '20px' : '15px'};
      border-radius: 50%;
      background: ${color};
      box-shadow: 0 0 20px ${color}, 0 0 40px ${color}40;
      animation: premiumGlow 1.5s ease-in-out infinite alternate;
    `;

    pulse.appendChild(innerPulse);
    pulseContainer.appendChild(pulse);

    const overlay = new window.google.maps.OverlayView();
    overlay.setMap(mapInstanceRef.current);
    overlay.draw = function() {
      const projection = this.getProjection();
      if (projection) {
        const point = projection.fromLatLngToDivPixel(position);
        if (point) {
          pulseContainer.style.left = point.x + 'px';
          pulseContainer.style.top = point.y + 'px';
        }
      }
    };
    overlay.onAdd = function() {
      const panes = this.getPanes();
      panes.overlayLayer.appendChild(pulseContainer);
    };

    pulseAnimationsRef.current.push({ element: pulseContainer, overlay });
    return overlay;
  };

  const addPremiumMarker = useCallback((position, type = 'start') => {
    if (!mapInstanceRef.current || !position) return;

    const icon = PREMIUM_ICONS[type];
    const marker = new window.google.maps.Marker({
      position,
      map: mapInstanceRef.current,
      icon,
      animation: window.google.maps.Animation.DROP,
      zIndex: type === 'moving' ? 1000 : 100
    });

    // Add premium pulse effect
    const colors = {
      start: '#10B981',
      destination: '#EF4444',
      hospital: '#3B82F6',
      moving: '#8B5CF6'
    };

    const pulseOverlay = createPremiumPulse(position, colors[type], type === 'hospital' ? 'small' : 'large');

    // Add premium info window
    const infoContent = {
      start: { title: '🚀 Journey Begins', desc: 'Your adventure starts here' },
      destination: { title: '🎯 Destination', desc: 'Your final stop awaits' },
      hospital: { title: '🏥 Medical Center', desc: 'Emergency care available' },
      moving: { title: '🚗 En Route', desc: 'Currently traveling' }
    };

    const info = infoContent[type];
    const infoWindow = new window.google.maps.InfoWindow({
      content: `
        <div style="
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 15px;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.2);
          border: none;
          min-width: 200px;
        ">
          <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600;">${info.title}</h3>
          <p style="margin: 0; opacity: 0.9; font-size: 14px;">${info.desc}</p>
        </div>
      `,
      pixelOffset: new window.google.maps.Size(0, -10)
    });

    marker.addListener('click', () => {
      infoWindow.open(mapInstanceRef.current, marker);
    });

    const markerData = { marker, overlay: pulseOverlay, infoWindow };
    markersRef.current.push(markerData);
    return markerData;
  }, []);

  const fetchPremiumHospitals = useCallback(() => {
    if (!mapInstanceRef.current || !showHospitals) return;

    const service = new window.google.maps.places.PlacesService(mapInstanceRef.current);
    const center = mapInstanceRef.current.getCenter();

    const request = {
      location: center,
      radius: '15000',
      type: ['hospital'],
      keyword: 'hospital emergency medical center'
    };

    service.nearbySearch(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        hospitalMarkersRef.current.forEach(marker => marker.setMap(null));
        hospitalMarkersRef.current = [];

        results.slice(0, 10).forEach(hospital => {
          const marker = new window.google.maps.Marker({
            position: hospital.geometry.location,
            map: mapInstanceRef.current,
            icon: PREMIUM_ICONS.hospital,
            animation: window.google.maps.Animation.DROP,
            zIndex: 50
          });

          createPremiumPulse(hospital.geometry.location, '#3B82F6', 'small');

          const infoWindow = new window.google.maps.InfoWindow({
            content: `
              <div style="
                background: linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%);
                color: white;
                padding: 20px;
                border-radius: 15px;
                box-shadow: 0 15px 35px rgba(0,0,0,0.2);
                max-width: 300px;
                border: none;
              ">
                <div style="display: flex; align-items: center; margin-bottom: 12px;">
                  <span style="font-size: 24px; margin-right: 10px;">🏥</span>
                  <h3 style="margin: 0; font-size: 18px; font-weight: 700;">${hospital.name}</h3>
                </div>
                <p style="margin: 8px 0; opacity: 0.9; line-height: 1.4;">${hospital.vicinity || 'Location details unavailable'}</p>
                ${hospital.rating ? `
                  <div style="display: flex; align-items: center; margin-top: 12px;">
                    <span style="color: #FCD34D; margin-right: 8px;">★</span>
                    <span style="font-weight: 600;">${hospital.rating.toFixed(1)}</span>
                    <span style="opacity: 0.7; margin-left: 5px;">/ 5.0</span>
                  </div>
                ` : ''}
                <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid rgba(255,255,255,0.2);">
                  <small style="opacity: 0.8;">🚨 Emergency services available 24/7</small>
                </div>
              </div>
            `,
            pixelOffset: new window.google.maps.Size(0, -10)
          });

          marker.addListener('click', () => {
            infoWindow.open(mapInstanceRef.current, marker);
          });

          hospitalMarkersRef.current.push(marker);
        });
      }
    });
  }, [showHospitals]);

  const createPremiumRoute = useCallback((start, end) => {
    if (!start || !end) return;

    const directionsService = new window.google.maps.DirectionsService();
    
    directionsService.route({
      origin: start,
      destination: end,
      travelMode: window.google.maps.TravelMode.DRIVING,
      avoidHighways: false,
      avoidTolls: false
    }, (response, status) => {
      if (status === "OK") {
        // Create premium gradient polyline
        const route = response.routes[0];
        const path = route.overview_path;
        
        // Clear existing renderer
        if (directionsRendererRef.current) {
          directionsRendererRef.current.setMap(null);
        }

        // Create multiple polylines for gradient effect
        const gradientColors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'];
        
        gradientColors.forEach((color, index) => {
          const polyline = new window.google.maps.Polyline({
            path: path,
            geodesic: true,
            strokeColor: color,
            strokeOpacity: 0.8 - (index * 0.15),
            strokeWeight: 8 - (index * 1.5),
            map: mapInstanceRef.current,
            zIndex: 10 - index
          });
        });

        const leg = route.legs[0];
        setRouteDetails({
          distance: leg.distance.text,
          duration: leg.duration.text,
          steps: leg.steps.length
        });

        // Fit bounds with padding
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(start);
        bounds.extend(end);
        mapInstanceRef.current.fitBounds(bounds, { padding: 100 });

        // Start route animation
        startRouteAnimation(path);
      }
    });
  }, []);

  const startRouteAnimation = (path) => {
    if (!path || path.length === 0) return;

    setIsNavigating(true);
    let step = 0;
    const totalSteps = path.length;

    const animate = () => {
      if (step < totalSteps) {
        const position = path[step];
        
        // Remove previous moving marker
        if (movingMarkerRef.current) {
          movingMarkerRef.current.setMap(null);
        }

        // Add new moving marker
        const movingMarker = addPremiumMarker(position, 'moving');
        movingMarkerRef.current = movingMarker.marker;

        // Update progress
        const progress = (step / totalSteps) * 100;
        setRouteProgress(progress);

        step++;
        animationFrameRef.current = setTimeout(animate, 100);
      } else {
        setIsNavigating(false);
        setRouteProgress(100);
      }
    };

    animate();
  };

  const initializePremiumMap = useCallback(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const defaultCenter = { lat: 9.005401, lng: 38.763611 };
    
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: defaultCenter,
      zoom: 12,
      styles: MAP_THEMES[mapTheme],
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: false,
      gestureHandling: 'greedy',
      backgroundColor: '#1a1a2e'
    });

    // Add custom controls
    const trafficLayer = new window.google.maps.TrafficLayer();
    if (showTraffic) trafficLayer.setMap(mapInstanceRef.current);

    // Add subtle map animations
    mapInstanceRef.current.addListener('idle', fetchPremiumHospitals);
    
    setMapLoaded(true);
  }, [mapTheme, showTraffic, fetchPremiumHospitals]);

  const updatePremiumMap = useCallback(() => {
    if (!mapInstanceRef.current) return;

    clearMarkers();

    if (startPoint && destination) {
      createPremiumRoute(startPoint, destination);
      addPremiumMarker(startPoint, 'start');
      addPremiumMarker(destination, 'destination');
    } else {
      if (startPoint) {
        addPremiumMarker(startPoint, 'start');
        mapInstanceRef.current.panTo(startPoint);
      }
      if (destination) {
        addPremiumMarker(destination, 'destination');
      }
      fetchPremiumHospitals();
    }
  }, [startPoint, destination, addPremiumMarker, createPremiumRoute, clearMarkers, fetchPremiumHospitals]);

  // Initialize map
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.GOOGLE_MAP_API_KEY}&libraries=places,geometry`;
      script.async = true;
      script.onload = () => {
        if (window.google && window.google.maps) {
          initializePremiumMap();
        }
      };
      document.head.appendChild(script);
    } else {
      initializePremiumMap();
    }

    return () => {
      if (animationFrameRef.current) clearTimeout(animationFrameRef.current);
      clearMarkers();
    };
  }, [initializePremiumMap, clearMarkers]);

  // Update map when points change
  useEffect(() => {
    if (mapLoaded && mapInstanceRef.current) {
      updatePremiumMap();
    }
  }, [startPoint, destination, mapLoaded, updatePremiumMap]);

  return (
    <div className="premium-map-container">
      {/* Premium Controls Panel */}
      <div className="premium-controls">
        <div className="control-group">
          <button 
            className={`control-btn ${mapTheme === 'premium' ? 'active' : ''}`}
            onClick={() => setMapTheme('premium')}
            title="Premium Dark Theme"
          >
            🌙
          </button>
          <button 
            className={`control-btn ${mapTheme === 'luxury' ? 'active' : ''}`}
            onClick={() => setMapTheme('luxury')}
            title="Luxury Theme"
          >
            ✨
          </button>
        </div>
        
        <div className="control-group">
          <button 
            className={`control-btn ${showTraffic ? 'active' : ''}`}
            onClick={() => setShowTraffic(!showTraffic)}
            title="Toggle Traffic"
          >
            🚦
          </button>
          <button 
            className={`control-btn ${showHospitals ? 'active' : ''}`}
            onClick={() => setShowHospitals(!showHospitals)}
            title="Toggle Hospitals"
          >
            🏥
          </button>
        </div>
      </div>

      {/* Premium Map */}
      <div ref={mapRef} className="premium-map" />

      {/* Premium Route Dashboard */}
      {routeDetails && (
        <div className="premium-dashboard">
          <div className="dashboard-header">
            <div className="status-indicator">
              <div className={`status-dot ${isNavigating ? 'active' : 'complete'}`}></div>
              <span>{isNavigating ? 'Navigating...' : 'Route Ready'}</span>
            </div>
          </div>
          
          <div className="route-metrics">
            <div className="metric">
              <div className="metric-icon">📍</div>
              <div className="metric-data">
                <span className="metric-value">{routeDetails.distance}</span>
                <span className="metric-label">Distance</span>
              </div>
            </div>
            
            <div className="metric">
              <div className="metric-icon">⏱️</div>
              <div className="metric-data">
                <span className="metric-value">{routeDetails.duration}</span>
                <span className="metric-label">Duration</span>
              </div>
            </div>
            
            <div className="metric">
              <div className="metric-icon">🗺️</div>
              <div className="metric-data">
                <span className="metric-value">{routeDetails.steps}</span>
                <span className="metric-label">Steps</span>
              </div>
            </div>
          </div>

          <div className="progress-section">
            <div className="progress-header">
              <span>Route Progress</span>
              <span>{Math.round(routeProgress)}%</span>
            </div>
            <div className="premium-progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${routeProgress}%` }}
              />
              <div className="progress-glow" style={{ left: `${routeProgress}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {!mapLoaded && (
        <div className="premium-loading">
          <div className="loading-spinner"></div>
          <p>Loading Premium Map Experience...</p>
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.startPoint?.lat === nextProps.startPoint?.lat &&
    prevProps.startPoint?.lng === nextProps.startPoint?.lng &&
    prevProps.destination?.lat === nextProps.destination?.lat &&
    prevProps.destination?.lng === nextProps.destination?.lng
  );
});

export default MapContainer;